import type { LucideIcon } from "lucide-react";
import type React from "react";
import { forwardRef, useMemo } from "react";
import type { IconType } from "react-icons";
import { cn } from "../../lib/utils";

type Props = {
  className?: string;
  placeholder?: string;
  type?: string;
  min?: number;
  max?: number;
  size?: "sm" | "md";
  onChange?: (v: string) => void;
  error?: boolean;
  disabled?: boolean;
  value?: string;
  defaultValue?: string;
  pointer?: boolean;
  borderless?: boolean;
  LeftComponent?: IconType | LucideIcon;
  RightComponent?: React.ReactNode;
};

// note forwardRef is required here to properly attach the parents ref to this input
export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  {
    className,
    placeholder,
    type,
    min,
    max,
    onChange,
    error,
    disabled,
    value,
    defaultValue,
    pointer,
    borderless,
    LeftComponent,
    RightComponent,
  },
  ref, // this is the forwarded ref, not part of props
) {
  const optionalAttributes = useMemo(() => {
    const attr: any = { value, disabled, defaultValue };
    if (max !== undefined) attr.max = max;
    if (min !== undefined) attr.min = min;
    return attr;
  }, [defaultValue, disabled, max, min, value]);

  return (
    <div className="relative mb-2 flex">
      {LeftComponent && (
        <span className="flex items-center justify-center rounded-md rounded-br-none rounded-tr-none border-b border-l border-t border-line-color bg-transparent px-3">
          <LeftComponent size={18} color="white" />
        </span>
      )}
      <input
        ref={ref}
        onChange={(e) => onChange?.(e.target.value)}
        type={type ?? "text"}
        className={cn(
          "px-3 py-2 focus:!border-primary-border focus:shadow-primary-border-shadow grow truncate rounded-md border border-line-color bg-transparent outline-none transition-all focus:shadow-[0_0_0_0.25rem]",
          pointer && "cursor-pointer",
          error && "!border-red !shadow-red-border-shadow !shadow-[0_0_0_0.25rem]",
          borderless && "!border-none",
          LeftComponent && "rounded-bl-none rounded-tl-none",
          className,
        )}
        placeholder={placeholder}
        min="0" // gets overridden if a real min is set on the optionalAttributes
        {...optionalAttributes}
      />
      {RightComponent && <span className="absolute right-0 h-full">{RightComponent}</span>}
    </div>
  );
});
